import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { slugify } from 'underscore.string'
import classNames from 'classnames'
import { Icon } from '@st-operations/uikit/src/v2'
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from 'react-accessible-accordion'
import { useLocation, useHistory } from 'react-router-dom'
import Storage from '@st-operations/uikit/Storage'
import decode from 'jwt-decode'

import ShareThisIcon from './sharethis-icon.svg'
import { useNavigationContext } from '../../contexts/NavigationContext'
import useUser from '../../hooks/useUser'
import useTeam from '../../hooks/useTeam'

import './styles.scss'
import Cookies from '_lib/cookies'

const Sidebar2 = ({ sections, options }) => {
  const {
    sectionIndex,
    setSectionIndex,
    sidebarRetracted,
    setSidebarRetracted,
    showSidebar,
  } = useNavigationContext()

  const { user } = useUser()
  const { team } = useTeam()
  const router = useHistory()
  const location = useLocation()

  const isClient = useMemo(() => {
    const token = Cookies.getCookie('token')
    if (token) {
      const data = decode(token)
      return data.org !== 'sharethis'
    }
  }, [])

  const hasPermission = useCallback(
    (item) => {
      // check user's permissions
      let hasPermission = isClient ? !!item.forClients : true
      if (!item.noAuth && user && user.acl) {
        const permissions = user.acl[team && team._id]

        if (permissions) {
          hasPermission = permissions[item.authKey ?? item.text]
        }
      }
      return hasPermission
    },
    [team, user, isClient]
  )

  const shouldRetract = (index) =>
    index === 0 || index === sections.length - 1 || index === sections.length - 2

  const onSelectSection = (index, onClick) => {
    setSectionIndex(index)
    setSidebarRetracted(shouldRetract(index))
    onClick && onClick()
  }

  const [expandedItems, setExpandedItems] = useState([])

  function handleAccordionChange(newExpandedItems) {
    setExpandedItems(newExpandedItems)
  }

  const [selectedSection, setSelectedSection] = useState()

  const matchesLocation = (item) => {
    const locationSplit = location.pathname.split('/')
    const linkSplit = item.link?.split('/') || []
    let result = linkSplit.length > 0
    for (let i = 0; i < linkSplit.length; i++) {
      result &= locationSplit[i] === linkSplit[i]
    }

    if (result) {
      return !sections
        .map((s) => s.groupsOrItems || [])
        .flat()
        .filter((f) => hasPermission(f))
        .find((f) => f.exact && f.link === location.pathname && f.link !== item.link)
    }

    return result
  }

  useEffect(() => {
    setSelectedSection()
    for (const section of ([...sections, ...options] || []).filter((f) =>
      isClient
        ? f.forClients || (f.groupsOrItems || []).filter((f2) => f2.forClients).length > 0
        : true
    )) {
      if (matchesLocation(section)) {
        setSelectedSection(section)
      } else {
        for (const item of (section.groupsOrItems || []).filter((f) => hasPermission(f))) {
          if (matchesLocation(item)) {
            setSelectedSection(section)
            return
          }
        }
      }
    }
  }, [sections, location.pathname, isClient])

  return (
    <If condition={showSidebar && selectedSection}>
      <nav
        className={classNames(
          'fixed bg-white shadow z-[101] h-full flex flex-col overflow-hidden py-2 text-gray-800',
          {
            'w-[calc(var(--sidebar-sections-width)+var(--sidebar-content-width))]': !sidebarRetracted,
            'w-[var(--sidebar-sections-width)]': sidebarRetracted,
          }
        )}
      >
        <Accordion
          className="flex flex-col"
          preExpanded={[`section-${slugify(selectedSection?.text)}`]}
          onChange={handleAccordionChange}
        >
          <For each="item" index="index" of={sections}>
            <If
              condition={
                !item.divider &&
                (item.groupsOrItems
                  ? item.groupsOrItems.filter((f) => hasPermission(f)).length > 0
                  : isClient
                  ? item.forClients
                  : true)
              }
            >
              <AccordionItem uuid={'section-' + slugify(item.text)}>
                <AccordionItemHeading>
                  <AccordionItemButton
                    className={classNames(
                      'cursor-pointer h-[40px] font-medium text-[14px] flex items-center justify-start px-[10px] hover:bg-gray-100',
                      { 'text-primary': selectedSection?.text === item.text }
                    )}
                  >
                    <div
                      className="contents"
                      onClick={() => {
                        if (item.onClick) {
                          item.onClick()
                        } else if (item.link && !matchesLocation(item)) {
                          router.push(item.link)
                        } else if (sidebarRetracted) {
                          onSelectSection(shouldRetract(sectionIndex) ? 1 : sectionIndex)
                        } else if (item.retracted !== undefined) {
                          setSidebarRetracted(true)
                        }
                      }}
                    >
                      <div
                        className={classNames('w-10 h-10 flex items-center justify-center', {
                          'border border-solid bg-primary-100 rounded-md border-primary':
                            selectedSection?.text === item.text,
                          'mr-2': !sidebarRetracted,
                        })}
                      >
                        <If condition={item.logo}>
                          <ShareThisIcon />
                        </If>
                        <If condition={item.icon}>
                          <Icon
                            icon={item.icon?.name}
                            type={item.icon?.type}
                            width={item.icon?.size}
                            height={item.icon?.size}
                            color="gray"
                          />
                        </If>
                      </div>
                      <div
                        className={classNames('flex-1', {
                          hidden: sidebarRetracted,
                        })}
                      >
                        {item.text}
                      </div>
                      <Icon
                        className={classNames('transition-all', {
                          'rotate-180': expandedItems.includes('section-' + slugify(item.text)),
                          hidden: sidebarRetracted || (item.groupsOrItems?.length || 0) === 0,
                        })}
                        type="fill"
                        icon="angledown"
                        width={24}
                        height={24}
                      />
                    </div>
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel
                  className={classNames('overflow-hidden', {
                    'h-0': sidebarRetracted || (item.groupsOrItems?.length || 0) === 0,
                  })}
                >
                  <div className={classNames('flex flex-col ml-14 mt-2')}>
                    <For each="subItem" of={item.groupsOrItems || []}>
                      <If condition={hasPermission(subItem)}>
                        <a
                          className={classNames(
                            'px-3 h-10 text-base flex items-center hover:bg-gray-100 text-gray-800',
                            {
                              'text-primary bg-primary-100 border-t-0 border-l-0 border-b-0 border-r-4 border-solid border-primary font-medium': matchesLocation(
                                subItem
                              ),
                            }
                          )}
                          href={subItem.link}
                          onClick={(e) => {
                            e.preventDefault()
                            router.push(subItem.link)
                          }}
                        >
                          {subItem.text}
                        </a>
                      </If>
                    </For>
                  </div>
                </AccordionItemPanel>
              </AccordionItem>
            </If>
          </For>
        </Accordion>
        <div className="flex-1" />
        <For each="option" of={options || []}>
          <If
            condition={
              option.retracted !== undefined ? option.retracted === sidebarRetracted : true
            }
          >
            <div
              className="cursor-pointer h-[40px] font-medium text-[14px] flex items-center justify-start px-[10px] hover:bg-gray-100 text-primary"
              onClick={() => {
                if (option.onClick) {
                  option.onClick()
                } else if (option.link && !matchesLocation(option)) {
                  router.push(option.link)
                } else if (sidebarRetracted) {
                  onSelectSection(shouldRetract(sectionIndex) ? 1 : sectionIndex)
                } else if (option.retracted !== undefined) {
                  setSidebarRetracted(true)
                }
              }}
            >
              <div
                className={classNames('w-10 h-10 flex items-center justify-center', {
                  'border border-solid bg-primary-100 rounded-md border-primary':
                    selectedSection?.text === option.text,
                  'mr-2': !sidebarRetracted,
                })}
              >
                <If condition={option.logo}>
                  <ShareThisIcon />
                </If>
                <If condition={option.icon}>
                  <Icon
                    icon={option.icon?.name}
                    type={option.icon?.type}
                    width={option.icon?.size}
                    height={option.icon?.size}
                    color="gray"
                  />
                </If>
              </div>
              <div
                className={classNames('flex-1', {
                  hidden: sidebarRetracted,
                })}
              >
                {option.text}
              </div>
            </div>
          </If>
        </For>
      </nav>
    </If>
  )
}

export default Sidebar2
